<template>
  <v-dialog
    v-if="room"
    ref="dialog"
    v-model="dialogOpened"
    max-width="500"
    scrollable
    persistent
    no-click-animation
    :retain-focus="false"
    @keydown.esc="hide"
  >
    <v-card>
      <v-btn class="v-dialog--close" icon @click="hide">
        <v-icon>fal fa-times</v-icon>
      </v-btn>
      <v-card-title class="headline pr-10">
        Datei freigeben
      </v-card-title>

      <v-card-text>

        <div v-if="loading" class="text-center">
          <v-progress-circular
            class="mt-4 mb-4"
            color="primary"
            size="30"
            indeterminate
          ></v-progress-circular>
          <div>Freigabe-Link wird erstellt ...</div>
        </div>

        <v-alert v-if="error" type="error" class="mb-0" text>
          Freigabe-Link konnte nicht erstellt werden.
        </v-alert>

        <div v-show="!loading && !error"><!--  use v-show for canvas -->
          <v-alert type="info" color="accent" text>
            Jeder mit diesem Freigabe-Link kann die Datei herunterladen.<br>
            <small>
              <strong>Hinweis:</strong>
              Der Empfänger muss weder dem Raum beigetreten noch registriert sein.
            </small>
          </v-alert>

          <h4>Variante 1: <span class="font-weight-regular">Link an E-Mail Adresse senden</span></h4>
          <v-form
            ref="emailForm"
            :readonly="sending"
            lazy-validation
            @submit.prevent="_sendEmail"
          >
            <input type="hidden" name="fileId" :value="file.id" />
            <v-text-field
              v-model="email"
              class="mt-6"
              name="email"
              placeholder="E-Mail Adresse eingeben"
              label="E-Mail Adresse"
              :rules="[rules.required, rules.email]"
              :error-messages="sending_errorMessage"
              :prepend-inner-icon="$vuetify.breakpoint.smAndUp ? 'far fa-at' : ''"
              :append-outer-icon="!sending ? 'far fa-paper-plane' : 'far fa-spinner-third fa-spin'"
              :readonly="sending"
              outlined
              @click:append-outer.prevent="_sendEmail"
            ></v-text-field>
          </v-form>

          <h4>Variante 2: <span class="font-weight-regular">Link selbst versenden</span></h4>
          <canvas ref="qrCode"></canvas>
          <code class="d-block text-center py-2 px-2" @click="_copy">{{href}}</code>
        </div>
      </v-card-text>

      <v-card-actions v-if="!loading && !error">
        <v-spacer></v-spacer>
        <v-btn :color="!shareable ? 'accent' : ''" text @click="_copy">
          Link kopieren
        </v-btn>
        <v-btn v-if="shareable" color="accent" text @click="_share">
          Teilen
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else-if="error && !loading">
        <v-spacer></v-spacer>
        <v-btn text @click="hide">Schließen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import QRCode from 'qrcode';

  import {copyTextToClipboard, formatFileSize, serializeForm} from '../../assets/js/utils';
  import eventBus from '../../assets/js/event-bus';
  import Dialog from './Dialog';


  export default {
    name: 'FilePublicShareDialog',

    extends: Dialog,

    data() {
      return {
        room: {},
        loading: false,
        error: false,
        file: {},
        filePublicId: '',
        sending: false,
        sending_errorMessage: '',
        email: '',
        rules: {
          required: (value) => {
            return !!value || 'E-Mail erforderlich';
          },
          email: (value) => {
            const input = document.createElement('input');
            input.type = 'email';
            input.value = value;
            let valid;
            try {
              valid = input.validity.valid;
            } catch (error) {
              valid = true;
            }
            return !!valid || 'E-Mail ungültig';
          }
        }
      };
    },

    computed: {
      href() {
        return `${location.origin}/file-public/${this.filePublicId}${this.file?.ext}`;
      },

      shareable() {
        return typeof navigator.share === 'function';
      }
    },

    watch: {
      dialogOpened(opened) {
        if (opened) {
          this.$nextTick(() => this._getFilePublicId());
        } else {
          this.$nextTick(() => this.$refs.emailForm.resetValidation());
        }
      }
    },

    mounted() {
      eventBus.$on('filePublicShare', this._onFilePublicShare);
    },

    destroyed() {
      eventBus.$off('filePublicShare', this._onFilePublicShare);
    },

    methods: {
      async _copy() {
        try {
          await copyTextToClipboard(this.href);
          eventBus.$emit('notifyUser', 'Link in die Zwischenablage kopiert');
        } catch (error) {
          eventBus.$emit('notifyUser', 'Link konnte nicht in die Zwischenablage kopiert werden');
        }
      },

      async _share() {
        if (!this.shareable) {
          return;
        }
        try {
          const {file} = this;
          const {userName} = this.$store.getters['share/getPeerSelf'].name;
          const title = 'XidrasTransfer Datei-Freigabe';

          await navigator.share({
            title,
            text: `${title}

${userName} hat eine Datei freigegeben:
${file.description ? `
Beschreibung: ${file.description}
` : ''}
${file.name} (${formatFileSize(file.size)})
${this.href}`
          });
          this.hide();
        } catch (error) {
          /* NOOP */
        }
      },

      async _sendEmail() {
        if (this.sending) {
          return;
        }

        const form = this.$refs.emailForm;
        const valid = form.validate();
        if (!valid) {
          return;
        }

        this.sending = true;
        this.sending_errorMessage = '';

        try {
          const data = serializeForm(form.$el);
          await this.$api.post('FileSendPublicDownloadEmail', data);
          this.email = '';
          this.hide();
          eventBus.$emit('notifyUser', 'Einladung versendet');

        } catch (error) {
          const errorMessage = 'E-Mail konnte nicht versendet werden. Versuche es später erneut.';
          this.sending_errorMessage = errorMessage;
        }

        this.sending = false;
      },

      async _getFilePublicId() {
        this.error = false;
        this.loading = true;
        this.sending_errorMessage = '';

        try {
          const {data: {publicId}} = await this.$api.post('FileGetPublicId', {id: this.file.id});
          this.filePublicId = publicId;
          await this._generateQrCode();
          this.loading = false;

        } catch (error) {
          this.filePublicId = '';
          this.error = true;
          this.loading = false;
        }
      },

      async _generateQrCode() {
        if (this._qrCode === this.href) {
          return;
        }
        await QRCode.toCanvas(this.$refs.qrCode, this.href);
        this._qrCode = this.href;
      },

      _onFilePublicShare(file) {
        this.file = {...file};
        this.show();
      }
    }
  };
</script>

<style scoped>
  canvas {
    display: block;
    margin: 0 auto;
  }

  code {
    color: inherit;
    white-space: nowrap;
    overflow-x: auto;
    box-shadow: none;
  }
</style>
